
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { formRules } from "@/utils/formRules";
import AddressGeo from "@/components/AddressGeo.vue";
import EditBtn from "@/components/generic/buttons/edit-btn.vue";

@Component({
  components: { EditBtn, AddressGeo },
})
export default class EditBuilding extends Vue {

  @Prop({ default: null })
  private initBuildingData: any;

  @Getter private isAdmin: boolean;
  @Getter private isMobile: boolean;

  private showDialog: boolean = false;
  private buildingId: number = null;
  private originalBuildingDataStringify: any = "";
  private originalUrmetCredentialDataStringify: any = "";
  private buildingData: any = {
    name: "",
    address: "",
    zipcode: "",
    city: "",
    country: "",
    latitude: "",
    longitude: "",
    registrationNumber: "",
    urmetSitId: null,
  };
  private valid: boolean = true;
  private loading: boolean = false;
  private displayAdvancedOptions: boolean = false;
  private urmetCredential: any = null;
  private loadingGetUrmetCredentials: boolean = false;
  private loadingDeleteUrmetCredentials: boolean = false;
  private rules = formRules;

  @Watch("initBuildingData", { immediate: true, deep: true })
  public async handlerBuildingData(newVal): Promise<void> {
    if (newVal?.id) {
      const { id, name, urmetSitId, registrationNumber } = newVal;
      this.buildingId = id;
      this.buildingData = { name };
      // address
      if (newVal?.Address) {
        const { address,
           zipcode,
           city,
           country,
           latitude,
           longitude } = newVal.Address;

        this.buildingData = { ...this.buildingData,
          address,
          zipcode,
          city,
          country,
          latitude,
          longitude,
          registrationNumber,
          urmetSitId };
      }
      this.originalBuildingDataStringify = JSON.stringify(this.buildingData);
      // urmet
      if (urmetSitId && this.canEditUrmetCredential) {
        this.urmetCredential = await this.getBuildingUrmetCredential();
        this.originalUrmetCredentialDataStringify = JSON.stringify(this.urmetCredential);
        this.toggleAdvancedOptions();
      }
    }
  }

  get canEditUrmetCredential(): boolean {
    return this.isAdmin;
  }

  get isUrmetCredentialModified(): boolean {
    return this.urmetCredential && (!this.originalUrmetCredentialDataStringify
      || JSON.stringify(this.urmetCredential) !== this.originalUrmetCredentialDataStringify);

  }

  get isModified(): boolean {
    return JSON.stringify(this.buildingData) !== this.originalBuildingDataStringify;
  }

  get canSubmit(): boolean {
    return this.valid && (this.isModified || this.isUrmetCredentialModified);
  }

  private reset(): void {
    this.showDialog = false;
    this.loading = false;
    this.handlerBuildingData(this.initBuildingData);
  }

  private toggleAdvancedOptions(): void {
    // At opening, reset urmet data
    if (!this.displayAdvancedOptions) {
      this.urmetCredential = this.originalUrmetCredentialDataStringify
        ? JSON.parse(this.originalUrmetCredentialDataStringify)
        : {};
    }
    this.displayAdvancedOptions = !this.displayAdvancedOptions;
  }

  private async editBuilding(): Promise<void> {
    this.loading = true;
    await this.$store.dispatch("buildings/editBuilding", {
      buildingId: this.buildingId,
      buildingData: {
        ...this.buildingData,
        urmetCredential: this.urmetCredential,
        urmetSitId: this.urmetCredential?.sitId,
      },
    }).then(() => {
      this.$store.commit("alerts/displaySuccess", {
        msg: this.$t("actions.saved"),
      });
      this.$emit("reset");
      this.reset();
    }).catch((error) => {
      this.$store.commit("alerts/displayHttpError", { error });
    }).finally(() => {
      this.loading = false;
    });
  }

  private async getBuildingUrmetCredential(): Promise<object> {
    this.loadingGetUrmetCredentials = true;
    return this.$store.dispatch("admin/buildings/getUrmetCredential", {
      buildingId: this.buildingId,
    }).then((data) => {
      return data || {};
    }).catch((error) => {
      this.$store.commit("alerts/displayHttpError", { error });
    }).finally(() => {
      this.loadingGetUrmetCredentials = false;
    });
  }

  private async deleteBuildingUrmetCredential(): Promise<void> {
    if (window.confirm("" + this.$t("Buildings.deleteUrmetCredential.confirm"))) {
      this.loadingDeleteUrmetCredentials = true;
      return this.$store.dispatch("admin/buildings/deleteUrmetCredential", {
        buildingId: this.buildingId,
      }).then(() => {
        this.$store.commit("alerts/displaySuccess", {
          msg: this.$t("actions.deleted"),
        });
        this.reset();
      }).catch((error) => {
        this.$store.commit("alerts/displayHttpError", { error });
      }).finally(() => {
        this.loadingDeleteUrmetCredentials = false;
      });
    }
  }
}
